import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '@/lib/session';
import { apiGet, apiPost } from '@/lib/api';
import { Avatar } from '@/components/ui/Avatar';
import { Icon } from '@/components/ui/Icon';
import { ChildSwitcher } from './ChildSwitcher';
import { BrandMark } from '@/components/ui/BrandMark';
import { BRAND } from '@/config/brand';
import { DEMO_CONFIG } from '@/config/demo';
import styles from './Topbar.module.css';

export function Topbar({ onToggleMobileNav, isMobileNavOpen }) {
  const { user, role, login, logout } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifsLoading, setNotifsLoading] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);

  const notifRef = useRef(null);
  const switcherRef = useRef(null);
  const avatarRef = useRef(null);
  const lastUnreadFetchRef = useRef(0);

  // Fetch unread count once on load & on route changes (throttled to max 1 per 60s)
  const fetchUnreadCount = useCallback(async () => {
    const now = Date.now();
    if (now - lastUnreadFetchRef.current < 60000) return;
    lastUnreadFetchRef.current = now;

    try {
      const res = await apiGet('/notifications/unread-count');
      if (res && typeof res.unread_count === 'number') {
        setUnreadCount(res.unread_count);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
  }, [location.pathname, fetchUnreadCount]);

  // Fetch notifications list ONLY when the dropdown is opened
  useEffect(() => {
    if (!notifOpen) return;

    let cancelled = false;
    setNotifsLoading(true);

    apiGet('/notifications?limit=10')
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.data || [];
        setNotifications(list);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setNotifsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [notifOpen]);

  // Close dropdowns on outside click or Esc
  useEffect(() => {
    function handleClickOutside(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
      if (switcherRef.current && !switcherRef.current.contains(e.target)) {
        setSwitcherOpen(false);
      }
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setAvatarMenuOpen(false);
      }
    }

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        setNotifOpen(false);
        setSwitcherOpen(false);
        setAvatarMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Close menus on route change
  useEffect(() => {
    setNotifOpen(false);
    setSwitcherOpen(false);
    setAvatarMenuOpen(false);
  }, [location.pathname]);

  function handleLogout() {
    setAvatarMenuOpen(false);
    navigate('/login', { replace: true });
    logout();
  }

  function handleSwitchUser(demoUser) {
    login({ id: demoUser.id, name: demoUser.name, role: demoUser.role });
    setSwitcherOpen(false);
    setAvatarMenuOpen(false);
    navigate(demoUser.defaultRoute, { replace: true });
  }

  async function handleMarkAllRead() {
    try {
      await apiPost('/notifications/read', { all: true });
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: 1 })));
    } catch {
      // ignore
    }
  }

  const roleHome = role ? `/${role}` : '/';

  const greeting = user
    ? `Welcome back, ${user.name.split(' ')[0]}`
    : BRAND.schoolName;

  return (
    <header className={styles.topbar}>
      <div className={styles.inner}>
        {/* Left: Mobile hamburger & Brand */}
        <div className={styles.leftGroup}>
          <button
            id="btn-mobile-nav-toggle"
            type="button"
            className={styles.hamburgerBtn}
            onClick={onToggleMobileNav}
            aria-label={isMobileNavOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={isMobileNavOpen}
            aria-controls="mobile-nav-tray"
          >
            <Icon name={isMobileNavOpen ? 'x' : 'menu'} size={22} />
          </button>

          <div className={styles.brandContainer}>
            <Link to={roleHome} className={styles.brandTitle} title={BRAND.productName}>
              <BrandMark size="sm" showSchool={false} />
            </Link>
          </div>

          <div className={styles.desktopGreeting}>
            <span className={styles.greetingText}>{greeting}</span>
          </div>
        </div>

        {/* Center: Desktop child switcher for parent role (>=768px) */}
        {role === 'parent' && (
          <div className={styles.desktopChildSwitcher}>
            <ChildSwitcher />
          </div>
        )}

        {/* Center: Desktop search bar (>=1024px) */}
        <div className={styles.searchGroup}>
          <Icon name="search" size={16} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search records, students, notices…"
            className={styles.searchInput}
            aria-label="Quick search"
          />
        </div>

        {/* Right: Controls */}
        <div className={styles.rightGroup}>
          {/* Quick User Switcher on desktop (>=768px) */}
          {DEMO_CONFIG.enabled && (
            <div className={styles.desktopSwitcherWrap} ref={switcherRef}>
              <button
                id="btn-quick-switch-user"
                type="button"
                className={styles.switchUserBtn}
                onClick={() => setSwitcherOpen((prev) => !prev)}
                title="Switch Demo Role"
                aria-label="Switch Demo Role"
              >
                <Icon name="users" size={16} />
                <span className={styles.switchUserLabel}>Switch Role</span>
              </button>

              {switcherOpen && (
                <div className={styles.popoverDropdown}>
                  <div className={styles.popoverHeader}>
                    <span className={styles.popoverTitle}>Demo User Switcher</span>
                  </div>
                  <div className={styles.userList}>
                    {DEMO_CONFIG.users.map((u) => {
                      const isCurrent = user?.id === u.id;
                      return (
                        <button
                          key={u.id}
                          type="button"
                          className={`${styles.userOption} ${isCurrent ? styles.activeUserOption : ''}`}
                          onClick={() => handleSwitchUser(u)}
                        >
                          <Avatar name={u.name} size="sm" />
                          <div className={styles.userInfo}>
                            <div className={styles.userNameText}>{u.name}</div>
                            <div className={styles.userSubtitleText}>{u.subtitle}</div>
                          </div>
                          {isCurrent && <span className={styles.activePill}>Active</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notification Bell with Unread Badge & Dropdown */}
          <div className={styles.popoverAnchor} ref={notifRef}>
            <button
              id="btn-topbar-bell"
              type="button"
              className={styles.bellBtn}
              onClick={() => setNotifOpen((prev) => !prev)}
              title="Notifications"
              aria-label="Notifications"
            >
              <Icon name="bell" size={20} />
              {unreadCount > 0 && (
                <span className={styles.unreadBadge}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <div className={styles.notifDropdown}>
                <div className={styles.popoverHeader}>
                  <span className={styles.popoverTitle}>Notifications</span>
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      className={styles.markReadBtn}
                      onClick={handleMarkAllRead}
                    >
                      Mark all read
                    </button>
                  )}
                </div>

                <div className={styles.notifList}>
                  {notifsLoading ? (
                    <div className={styles.emptyNotifs}>Loading notifications…</div>
                  ) : notifications.length === 0 ? (
                    <div className={styles.emptyNotifs}>No new notifications</div>
                  ) : (
                    notifications.slice(0, 5).map((n) => (
                      <div
                        key={n.id}
                        className={`${styles.notifItem} ${!n.read ? styles.unreadNotifItem : ''}`}
                      >
                        <div className={styles.notifHeaderRow}>
                          <span className={styles.notifTitle}>{n.title}</span>
                          <span className={styles.notifTime}>
                            {n.created_at
                              ? new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                              : 'Recently'}
                          </span>
                        </div>
                        <p className={styles.notifBody}>{n.body}</p>
                      </div>
                    ))
                  )}
                </div>

                <div className={styles.popoverFooter}>
                  <Link
                    to={role ? `/${role}/notifications` : '/login'}
                    className={styles.viewAllLink}
                    onClick={() => setNotifOpen(false)}
                  >
                    View all notifications →
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* User Profile & Avatar Menu */}
          {user && (
            <div className={styles.avatarMenuAnchor} ref={avatarRef}>
              {/* Mobile avatar tap (<768px): Toggles avatar sheet/menu */}
              <button
                type="button"
                className={styles.mobileAvatarBtn}
                onClick={() => setAvatarMenuOpen((prev) => !prev)}
                title="Open user menu"
                aria-label="Open user menu"
                aria-expanded={avatarMenuOpen}
              >
                <Avatar name={user.name} size="sm" />
              </button>

              {/* Desktop Profile Link + Logout (>=768px) */}
              <div className={styles.desktopUserGroup}>
                <Link
                  to={role ? `/${role}/profile` : '/login'}
                  title="My Profile"
                  aria-label="My Profile"
                  className={styles.desktopProfileLink}
                >
                  <Avatar name={user.name} size="md" />
                </Link>
                <button
                  type="button"
                  className={styles.desktopLogoutBtn}
                  onClick={handleLogout}
                  title="Exit Session"
                  aria-label="Exit Session"
                >
                  <Icon name="logout" size={18} />
                </button>
              </div>

              {/* Avatar Dropdown / Sheet on mobile */}
              {avatarMenuOpen && (
                <div className={styles.avatarDropdown}>
                  <div className={styles.avatarHeader}>
                    <Avatar name={user.name} size="md" />
                    <div className={styles.avatarHeaderText}>
                      <div className={styles.avatarName}>{user.name}</div>
                      <div className={styles.avatarRole}>
                        {role ? role.charAt(0).toUpperCase() + role.slice(1).toLowerCase() : 'User'}
                      </div>
                    </div>
                  </div>

                  <div className={styles.avatarActionList}>
                    <Link
                      to={role ? `/${role}/profile` : '/login'}
                      className={styles.avatarMenuItem}
                      onClick={() => setAvatarMenuOpen(false)}
                    >
                      <Icon name="user" size={16} />
                      <span>My Profile</span>
                    </Link>
                  </div>

                  {DEMO_CONFIG.enabled && (
                    <div className={styles.avatarDemoSection}>
                      <div className={styles.avatarSectionLabel}>Switch Demo Role</div>
                      <div className={styles.avatarUserGrid}>
                        {DEMO_CONFIG.users.map((u) => {
                          const isCurrent = user?.id === u.id;
                          return (
                            <button
                              key={u.id}
                              type="button"
                              className={`${styles.avatarUserPill} ${isCurrent ? styles.activeUserOption : ''}`}
                              onClick={() => handleSwitchUser(u)}
                            >
                              <Avatar name={u.name} size="sm" />
                              <div className={styles.userInfo}>
                                <div className={styles.userNameText}>{u.name}</div>
                                <div className={styles.userSubtitleText}>{u.subtitle}</div>
                              </div>
                              {isCurrent && <span className={styles.activePill}>Active</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className={styles.avatarFooter}>
                    <button
                      type="button"
                      className={styles.avatarLogoutBtn}
                      onClick={handleLogout}
                    >
                      <Icon name="logout" size={16} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Sub-row for Parent Child Switcher (<768px) */}
      {role === 'parent' && (
        <div className={styles.mobileSubRow}>
          <ChildSwitcher fullWidth />
        </div>
      )}
    </header>
  );
}

// Aliases for backwards compatibility
export const Masthead = Topbar;
